import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import {
	Button,
	Form,
	FormGroup,
	Row,
	Col,
	Input,
	FormFeedback,
	Label,
} from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2"; // Import SweetAlert2
import userService from "services/UserService";
import objectiveActionPlansService from "services/ObjectiveActionPlansService";
import { FloppyDisk, SignOut } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const statusOptions = [
	{ value: "ANDAMENTO", label: "Em Andamento" },
	{ value: "STANDBY", label: "Standby" },
	{ value: "OK", label: "Ok" },
	{ value: "CANCELADO", label: "Cancelado" },
];

const ModalCreateEditObjective = ({
	show,
	onCloseClick,
	data,
	action_plans_id,
	action_plan_deadline,
	getObjetivos, // Ensure this prop is passed if it's used
	allowedResponsibleUserIds, // IDs dos usuários que podem ver o plano (quem pode ser responsável)
}) => {
	const id = data?.id;
	const [formData, setFormData] = useState({
		objective: "",
		status: "",
		deadline: "",
		generates_cost: 0,
		active: 1,
		responsible_id: "",
		action_plans_id: action_plans_id,
	});
	const [formErrors, setFormErrors] = useState({});
	const [usuarios, setUsuarios] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const fetchUsuarios = async () => {
		try {
			const response = await userService.get();
			let funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			// Se vier lista de quem pode ver o plano, restringe o select de responsável a esses usuários
			if (allowedResponsibleUserIds && allowedResponsibleUserIds.length > 0) {
				const idSet = new Set(allowedResponsibleUserIds.map(String));
				funcoes = funcoes.filter((opt) => idSet.has(String(opt.value)));
			}
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (show) {
			fetchUsuarios();

			// Reset form data when modal is opened
			setFormData({
				objective: data?.objective || "",
				status: data?.status || "",
				deadline: data?.deadline || "",
				generates_cost: data?.generates_cost || 0,
				active: data?.active || 1,
				responsible_id: data?.responsible?.id || "",
				action_plans_id: data?.action_plans_id || action_plans_id,
			});

			// Clear form errors
			setFormErrors({});
		}
	}, [show, allowedResponsibleUserIds]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevFormData) => ({
			...prevFormData,
			[name]: value,
		}));
	};

	const handleSelectChange = (selectedOption, fieldName) => {
		const selectedValue = selectedOption ? selectedOption.value : "";
		setFormData((prevFormData) => ({
			...prevFormData,
			[fieldName]: selectedValue,
		}));
	};

	const validate = () => {
		const errors = {};
		if (!formData.objective) {
			errors.objective = "Por favor, informe o objetivo!";
		}
		if (!formData.responsible_id) {
			errors.responsible_id = "Por favor, informe o responsável!";
		}
		if (!formData.deadline) {
			errors.deadline = "Por favor, informe o prazo!";
		} else if (action_plan_deadline && formData.deadline > action_plan_deadline) {
			errors.deadline = "O prazo do objetivo não pode ser maior que o prazo do plano de ação!";
		}

		if (!formData.status) {
			errors.status = "Por favor, informe o status!";
		}
		// Adicione outras validações se necessário
		return errors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
		} else {
			setIsLoading(true);
			try {
				if (id) {
					await objectiveActionPlansService.put(id, formData);
				} else {
					await objectiveActionPlansService.post(formData);
				}

				await getObjetivos();

				// Show success swal and close modal when OK is clicked
				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Operação realizada com sucesso!",
					confirmButtonText: "OK",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				}).then(() => {
					onCloseClick(); // Close the modal
				});
			} catch (error) {
				console.log(error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<Modal
			size="sm"
			isOpen={show}
			toggle={onCloseClick}
			centered={true}
			style={{ minWidth: 800 }}
		>
			<div className="modal-content">
				<ModalBody className="px-4 py-3">
					<Col md="12">
						<Form onSubmit={handleSubmit}>
							<FormGroup className="mb-3">
								<Label htmlFor="objective" md="2">
									Objetivo{" "}
									<span style={{ color: "red" }}>*</span>
								</Label>
								<Input
									name="objective"
									type="textarea"
									placeholder="Insira o objetivo."
									onChange={handleInputChange}
									value={formData.objective}
									invalid={!!formErrors.objective}
								/>
								{formErrors.objective && (
									<FormFeedback>
										{formErrors.objective}
									</FormFeedback>
								)}
							</FormGroup>
							<Row>
								<Col md="4">
									<FormGroup className="mb-3">
										<Label htmlFor="responsible_id">
											Responsável{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Select
											styles={customStyles}
											placeholder="Selecione o responsável"
											onChange={(selectedOption) =>
												handleSelectChange(
													selectedOption,
													"responsible_id",
												)
											}
											value={usuarios.find(
												(option) =>
													option.value ===
													formData.responsible_id,
											)}
											options={usuarios}
										/>
										{formErrors.responsible_id && (
											<div
												style={{
													color: "red",
													marginTop: 5,
												}}
											>
												{formErrors.responsible_id}
											</div>
										)}
									</FormGroup>
								</Col>

								<Col md="4">
									<FormGroup className="mb-3">
										<Label htmlFor="status">
											Status{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Select
											styles={customStyles}
											placeholder="Selecione o status"
											onChange={(selectedOption) =>
												handleSelectChange(
													selectedOption,
													"status",
												)
											}
											value={statusOptions.find(
												(option) =>
													option.value ===
													formData.status,
											)}
											options={statusOptions}
											invalid={!!formErrors.status}
										/>
										{formErrors.status && (
											<div
												style={{
													color: "red",
													marginTop: 5,
												}}
											>
												{formErrors.status}
											</div>
										)}
									</FormGroup>
								</Col>
								<Col md="4">
									<FormGroup className="mb-3">
										<Label htmlFor="deadline">
											Prazo{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Input
											name="deadline"
											placeholder="00/00/0000"
											type="date"
											className="form-control"
											id="deadline"
											onChange={handleInputChange}
											value={formData.deadline}
											invalid={!!formErrors.deadline}
										/>
										{formErrors.deadline && (
											<FormFeedback>
												{formErrors.deadline}
											</FormFeedback>
										)}
									</FormGroup>
								</Col>
							</Row>

							<Row className="py-1">
								<div className="hstack gap-2 justify-content-center mb-0">
									<Button
										style={{
											background: "none",
											padding: 10,
											cursor: "pointer",
											color: "black",
											display: "flex",
											width: 112,
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
										}}
										type="submit"
										disabled={isLoading}
									>
										{isLoading ? (
											<span
												className="spinner-border spinner-border-sm mr-1"
												style={{ marginRight: 10 }}
											></span>
										) : null}
										<FloppyDisk size={16} /> Salvar
									</Button>
									<Button
										type="button"
										style={{
											background: "none",
											padding: 10,
											cursor: "pointer",
											color: "black",
											width: 112,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
										}}
										onClick={onCloseClick}
									>
										<SignOut size={16} /> Fechar
									</Button>
								</div>
							</Row>
						</Form>
					</Col>
				</ModalBody>
			</div>
		</Modal>
	);
};

ModalCreateEditObjective.propTypes = {
	show: PropTypes.bool,
	onCloseClick: PropTypes.func,
	data: PropTypes.any,
	action_plans_id: PropTypes.any,
	action_plan_deadline: PropTypes.string,
	getObjetivos: PropTypes.func.isRequired,
	allowedResponsibleUserIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default ModalCreateEditObjective;
